import type { ContentAsset } from "./types";
import { trackAssetResolution, trackAssetGcsFallback, type AssetContentSource } from "./contentSource";
import { getGcsAssetUrl } from "./gcsConfig";
import { resolveCmsAssetPath } from "./assetMap";
import { resolveProjectAssetPath } from "./projectAssetMap";

function isProjectOnlyPath(path: string): boolean {
  return (
    path.includes("/icons/") ||
    path.includes("/hero-five/") ||
    path.includes("/hero-six/") ||
    path.includes("inverted-comma") ||
    path.includes("carousel-arrow")
  );
}

/** Legacy JSON paths that pointed at shared/icons but live under home/hero-two in the bucket. */
const CMS_ASSET_ALIASES: Record<string, string> = {
  "assets/shared/icons/nationwide-coverage.svg": "assets/home/hero-two/images/nationwide-coverage.svg",
  "assets/shared/icons/eligibility-criteria.svg": "assets/home/hero-two/images/eligibility-criteria.svg",
  "assets/shared/icons/selection-process.png": "assets/home/hero-two/images/selection-process.png",
  "assets/shared/icons/key-focus-area.png": "assets/home/hero-two/images/key-focus-area.png",
};

function normalizeCmsAssetPath(path: string): string {
  return CMS_ASSET_ALIASES[path] ?? path;
}

export interface ResolvedCmsAsset {
  src: string;
  fallbackSrc?: string;
  source: AssetContentSource;
  path: string;
}

function resolveCmsPath(path: string): ResolvedCmsAsset {
  const cmsPath = normalizeCmsAssetPath(path);

  if (isProjectOnlyPath(path)) {
    const projectSrc = resolveProjectAssetPath(path);
    if (projectSrc) {
      trackAssetResolution(path, "project", projectSrc);
      return { src: projectSrc, source: "project", path };
    }
  }

  const local = resolveCmsAssetPath(cmsPath);
  const gcs = getGcsAssetUrl(cmsPath);

  if (local && !gcs) {
    trackAssetResolution(cmsPath, "local", local);
    return { src: local, source: "local", path: cmsPath };
  }

  // GCS first when configured; local bundled file is fallback (e.g. img onerror)
  if (gcs) {
    trackAssetResolution(cmsPath, "gcs", gcs);
    return {
      src: gcs,
      fallbackSrc: local,
      source: "gcs",
      path: cmsPath,
    };
  }

  if (local) {
    trackAssetResolution(cmsPath, "local", local);
    return { src: local, source: "local", path: cmsPath };
  }

  const project = resolveProjectAssetPath(cmsPath);
  if (project) {
    trackAssetResolution(cmsPath, "project", project);
    return { src: project, source: "project", path: cmsPath };
  }

  return { src: cmsPath, source: "local", path: cmsPath };
}

export function resolveCmsAsset(asset?: ContentAsset | string | null): ResolvedCmsAsset {
  if (!asset) {
    return { src: "", source: "local", path: "" };
  }

  if (typeof asset === "string") {
    if (asset.startsWith("http://") || asset.startsWith("https://")) {
      return { src: asset, source: "gcs", path: asset };
    }
    return resolveCmsPath(asset);
  }

  const url = asset.url;
  if (!url) {
    return { src: "", source: "local", path: "" };
  }

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return { src: url, source: "gcs", path: url };
  }

  return resolveCmsPath(url);
}

/** Primary image/video URL — bundled in dev by default; GCS in production. */
export function resolveAsset(asset?: ContentAsset | string | null): string {
  const resolved = resolveCmsAsset(asset);
  return resolved.src;
}

/** Bundled local URL if GCS image fails to load. */
export function resolveAssetLocalFallback(asset?: ContentAsset | string | null): string | undefined {
  return resolveCmsAsset(asset).fallbackSrc;
}

export function resolveAssetAlt(asset?: ContentAsset | null, fallback = ""): string {
  if (!asset || typeof asset === "string") return fallback;
  return asset.alt ?? fallback;
}

/** Use on <img> for GCS-first with automatic local fallback on 404. */
export function cmsImageProps(asset?: ContentAsset | string | null): {
  src: string;
  onError?: (event: { currentTarget: HTMLImageElement }) => void;
} {
  const resolved = resolveCmsAsset(asset);
  if (!resolved.fallbackSrc) {
    return { src: resolved.src };
  }

  return {
    src: resolved.src,
    onError: (event) => {
      const img = event.currentTarget;
      if (img.src !== resolved.fallbackSrc) {
        trackAssetGcsFallback(resolved.path, resolved.fallbackSrc!);
        img.src = resolved.fallbackSrc!;
      }
    },
  };
}
